import { css } from '@emotion/react';
import {
  Button,
  Card,
  color,
  Icon,
  iconSize,
  spacing,
  Typography
} from '@uniquegood/realworld-studio-design';
import { useMemo, useState } from 'react';
import download from 'downloadjs';
import { RowFlex, JustifySpaceBetween } from '@/styles';
import { useRequest } from '@/hooks';
import meatballIcon from '@/pages/dashboard/assets/meatball.svg';
import { coreApi } from '@/api';
import { ClickEventName, track } from '@/track';

interface UserVariableSectionProps {
  appId?: string;
  projectId?: string;
  canDownload?: boolean;
}

const ITEMS_PER_PAGE = 10;

export default function UserVariableSection({
  appId,
  projectId,
  canDownload = false
}: UserVariableSectionProps) {
  const [currentPage, setCurrentPage] = useState(1);

  const { data: applicationInfo, mutate: mutateApplicationInfo } = useRequest<
    CommonResponseModel<ApplicationInfoResponseModel>
  >(`/apps/${appId}/info`);
  const { data: projectDataResponse, mutate: mutateProjectData } = useRequest<
    CommonResponseModel<ProjectV2>
  >(
    `/apps/${appId}/projects/${projectId}`,
    {},
    {
      headers: {
        'x-rwd-api-version': '1.1'
      }
    }
  );
  const projectData = projectDataResponse?.data;
  const { data: me, mutate: mutateMe } = useRequest<MyInfo>('/api/me');

  const apiUrl = useMemo(() => {
    if (!projectId) return '';
    return `/apps/${appId}/projects/${projectId}/statistics/variables?page=${currentPage}&take=${ITEMS_PER_PAGE}`;
  }, [appId, projectId, currentPage]);

  const { data: { data: variableData } = {}, isValidating: isLoading } = useRequest<
    CommonResponseModel<UserVariableStatisticsResponseModelPaginatedResponse>
  >(apiUrl, {
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false
  });

  const variableList = variableData?.data ?? [];
  const totalPages = variableData?.totalPages ?? 0;

  const handleDownload = async () => {
    if (!appId || !projectId) return;

    const response = await coreApi.get<Blob>(
      `/apps/${appId}/projects/${projectId}/statistics/variables/download`,
      { responseType: 'blob' }
    );

    const contentDisposition = response.headers['content-disposition'];
    const fileNameMatch = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(
      contentDisposition || ''
    );
    const rawFileName = fileNameMatch?.[1] || fileNameMatch?.[2];

    const fileName = rawFileName ? decodeURIComponent(rawFileName) : `user_variables_${projectId}.xlsx`;

    download(response.data, fileName);

    let applicationInfoData = applicationInfo;
    let projectDataData = projectData;
    let meInfo = me;

    if (!applicationInfo) {
      applicationInfoData = await mutateApplicationInfo();
    }

    if (!projectData) {
      const mutatedData = await mutateProjectData();
      projectDataData = mutatedData?.data;
    }

    if (!meInfo) {
      meInfo = await mutateMe();
    }

    const param = {
      channel_id: appId,
      channel_name: applicationInfoData?.data.name,
      use_purpose_type: applicationInfoData?.data.usePurposeType,
      plan_type: applicationInfoData?.data.planType,
      project_id: projectId,
      project_name: projectDataData?.name,
      user_id: meInfo?.id,
      dashboard_type: '유저 입력 변수'
    };

    track.onClick({
      clickEventName: ClickEventName.click_dashboard_button_download,
      params: param
    });
  };

  const formatDateTime = (dateString?: string | null) => {
    if (!dateString) return '-';

    return new Date(dateString).toLocaleString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  };

  const getPageNumbers = () => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const pages: (number | string)[] = [];

    if (currentPage <= 3) {
      pages.push(1, 2, 3, 4, 5, 'ellipsis-end', totalPages);
    } else if (currentPage >= totalPages - 2) {
      pages.push(
        1,
        'ellipsis-start',
        totalPages - 4,
        totalPages - 3,
        totalPages - 2,
        totalPages - 1,
        totalPages
      );
    } else {
      pages.push(
        1,
        'ellipsis-start',
        currentPage - 1,
        currentPage,
        currentPage + 1,
        'ellipsis-end',
        totalPages
      );
    }

    return pages;
  };

  return (
    <Card cssStyle={CardStyle}>
      <div css={[RowFlex, JustifySpaceBetween, HeaderStyle]}>
        <Typography as="h3" type="heading">
          유저 입력 변수
        </Typography>
        <Button icon="download_solid" type="plain" onClick={handleDownload} disabled={!canDownload}>
          다운로드
        </Button>
      </div>

      <div css={TableContainerStyle}>
        <table css={TableStyle}>
          <thead>
            <tr>
              <th css={ProfileColumnStyle}>프로필</th>
              <th css={UserColumnStyle}>유저 닉네임</th>
              <th css={VariableNameColumnStyle}>변수명</th>
              <th css={VariableFullNameColumnStyle}>변수 키</th>
              <th>입력 값</th>
              <th css={UpdatedAtColumnStyle}>마지막 수정 시각</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={6} css={LoadingCellStyle}>
                  <div css={LoadingContentStyle}>
                    <Typography type="body" color={color.text_subdued}>
                      데이터를 불러오는 중...
                    </Typography>
                  </div>
                </td>
              </tr>
            )}

            {!isLoading && variableList.length === 0 && (
              <tr>
                <td colSpan={6} css={LoadingCellStyle}>
                  <div css={LoadingContentStyle}>
                    <Typography type="body" color={color.text_subdued}>
                      입력된 변수 데이터가 없습니다
                    </Typography>
                  </div>
                </td>
              </tr>
            )}

            {!isLoading &&
              variableList.length > 0 &&
              variableList.map((variable) => (
                <tr key={`${variable.userId}-${variable.variableFullName}`}>
                  <td css={ProfileCellStyle}>
                    <div css={AvatarStyle}>
                      {variable.userProfileImageUrl ? (
                        <img src={variable.userProfileImageUrl} alt={variable.userName || 'User'} />
                      ) : (
                        <Icon
                          icon="check_solid"
                          size={iconSize.medium}
                          color={color.icon_subdued}
                        />
                      )}
                    </div>
                  </td>
                  <td>
                    <Typography type="body">{variable.userName || '{유저 닉네임}'}</Typography>
                  </td>
                  <td>
                    <Typography type="body">{variable.variableName || '-'}</Typography>
                  </td>
                  <td>
                    <Typography type="body" cssStyle={CodeStyle}>
                      {variable.variableFullName || '-'}
                    </Typography>
                  </td>
                  <td>
                    <Typography type="body" cssStyle={ValueStyle}>
                      {variable.value || '-'}
                    </Typography>
                  </td>
                  <td>
                    <Typography type="body">{formatDateTime(variable.updatedAt)}</Typography>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div css={PaginationStyle}>
          <button
            type="button"
            disabled={currentPage === 1}
            onClick={() => setCurrentPage((prev) => prev - 1)}
            css={PageNavButtonStyle}
          >
            <Icon
              icon="chevron_right_regular"
              size={iconSize.small}
              color={currentPage === 1 ? color.icon_disabled : color.icon_subdued}
              css={css`
                transform: rotate(-180deg);
              `}
            />
          </button>

          {getPageNumbers().map((page) => {
            if (typeof page === 'string') {
              return (
                <div key={page} css={EllipsisStyle}>
                  <img src={meatballIcon} alt="meatball" />
                </div>
              );
            }

            return (
              <button
                key={`page-${page}`}
                type="button"
                css={[PageNumberButtonStyle, currentPage === page && PageNumberActiveStyle]}
                onClick={() => setCurrentPage(page)}
              >
                {page}
              </button>
            );
          })}

          <button
            type="button"
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage((prev) => prev + 1)}
            css={PageNavButtonStyle}
          >
            <Icon
              icon="chevron_right_regular"
              size={iconSize.small}
              color={currentPage === totalPages ? color.icon_disabled : color.icon_subdued}
            />
          </button>
        </div>
      )}
    </Card>
  );
}

const CardStyle = css`
  max-width: 1300px;
  min-width: 740px;
  margin-bottom: ${spacing.margin.xlarge4};
`;

const HeaderStyle = css`
  margin-bottom: ${spacing.margin.xlarge2};
  align-items: center;
`;

const TableContainerStyle = css`
  border: 1px solid ${color.border_default_subdued};
  border-radius: ${spacing.borderRadius.large};
  overflow: hidden;
`;

const TableStyle = css`
  width: 100%;
  border-collapse: collapse;

  th,
  td {
    padding: ${spacing.padding.medium};
    text-align: left;
    border-bottom: 1px solid ${color.border_default_subdued};
    vertical-align: top;
  }

  th {
    background-color: ${color.surface_default_subdued};
    color: ${color.text_default};
    font-weight: 500;
    font-size: 14px;
  }

  tbody tr:hover {
    background-color: ${color.surface_default_hovered};
  }

  tbody tr:last-child td {
    border-bottom: none;
  }
`;

const ProfileColumnStyle = css`
  width: 69px;
`;

const UserColumnStyle = css`
  width: 140px;
`;

const VariableNameColumnStyle = css`
  width: 120px;
`;

const VariableFullNameColumnStyle = css`
  width: 220px;
`;

const UpdatedAtColumnStyle = css`
  width: 190px;
`;

const ProfileCellStyle = css`
  padding: ${spacing.padding.small} ${spacing.padding.medium} !important;
`;

const AvatarStyle = css`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  overflow: hidden;
  background-color: ${color.surface_default_subdued};
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const CodeStyle = css`
  color: ${color.text_subdued};
  word-break: break-all;
`;

const ValueStyle = css`
  white-space: pre-wrap;
  word-break: break-word;
`;

const PaginationStyle = css`
  ${RowFlex}
  justify-content: center;
  align-items: center;
  gap: ${spacing.margin.medium};
  margin-top: ${spacing.margin.xlarge2};
  padding: ${spacing.padding.xsmall} ${spacing.padding.small};
`;

const PageNavButtonStyle = css`
  border: none;
  background: transparent;
  cursor: pointer;
  padding: ${spacing.padding.xsmall};
  border-radius: ${spacing.borderRadius.small};
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;

  &:hover:not(:disabled) {
    background-color: ${color.surface_default_hovered};
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

const PageNumberButtonStyle = css`
  border: none;
  background: transparent;
  cursor: pointer;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  color: ${color.text_subdued};
  border-radius: ${spacing.borderRadius.small};

  &:hover {
    background-color: ${color.surface_default_hovered};
  }
`;

const PageNumberActiveStyle = css`
  background-color: ${color.surface_default_subdued};
  color: ${color.text_default};
`;

const EllipsisStyle = css`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LoadingCellStyle = css`
  text-align: center;
  padding: ${spacing.padding.xlarge3} !important;
  border-bottom: none !important;
`;

const LoadingContentStyle = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${spacing.margin.medium};
  min-height: 200px;
`;
